const ENQUIRY_DRAWER_GSAP_DEFAULTS = {
  duration: {
    overlay: 0.4,
    drawerEnter: 0.72,
    drawerExit: 0.52,
    drawerContent: 0.46,
  },
  ease: {
    in: 'power2.in',
    out: 'power2.out',
    emphasisOut: 'power3.out',
    emphasisInOut: 'power3.inOut',
  },
  stagger: {
    drawerItems: 0.055,
  },
};

function getEnquiryDrawerMotionConfig() {
  const globalTokens = window.KM_GSAP_TOKENS || {};
  const globalDuration = globalTokens.duration || {};
  const globalEase = globalTokens.ease || {};
  const globalStagger = globalTokens.stagger || {};
  return {
    duration: {
      ...ENQUIRY_DRAWER_GSAP_DEFAULTS.duration,
      overlay: globalDuration.overlay ?? ENQUIRY_DRAWER_GSAP_DEFAULTS.duration.overlay,
      drawerEnter: globalDuration.drawerEnter ?? ENQUIRY_DRAWER_GSAP_DEFAULTS.duration.drawerEnter,
      drawerExit: globalDuration.drawerExit ?? ENQUIRY_DRAWER_GSAP_DEFAULTS.duration.drawerExit,
      drawerContent: globalDuration.drawerContent ?? ENQUIRY_DRAWER_GSAP_DEFAULTS.duration.drawerContent,
    },
    ease: {
      ...ENQUIRY_DRAWER_GSAP_DEFAULTS.ease,
      ...globalEase,
    },
    stagger: {
      ...ENQUIRY_DRAWER_GSAP_DEFAULTS.stagger,
      drawerItems: globalStagger.drawerItems ?? ENQUIRY_DRAWER_GSAP_DEFAULTS.stagger.drawerItems,
    },
  };
}

class EnquiryDrawer extends HTMLElement {
  constructor() {
    super();

    this.gsapTimeline = null;
    this.isOpen = false;
    this.activeElement = null;
    this.overlay = this.querySelector('#EnquiryDrawer-Overlay');
    this.drawerInner = this.querySelector('.drawer__inner');
    this.productField = this.querySelector('[data-enquiry-product-field]');
    this.urlField = this.querySelector('[data-enquiry-url-field]');
    this.typeField = this.querySelector('[data-enquiry-type-field]');
    this.heading = this.querySelector('[data-enquiry-heading]');
    this.intro = this.querySelector('[data-enquiry-intro]');
    this.submitLabel = this.querySelector('[data-enquiry-submit-label]');
    this.currentTitle = '';
    this.mode = 'enquire';

    // Single contact form for both modes. It submits NATIVELY so Shopify's
    // reCAPTCHA token is attached (a fetch POST is rejected, and the customer
    // newsletter form silently fails inside the drawer). Win mode just relabels
    // the fields and tags the contact email as a competition entry.
    this.form = this.querySelector('form');

    this.onOverlayClick = this.close.bind(this);
    this.onEscape = (evt) => evt.code === 'Escape' && this.close();
    this.onTriggerClick = this.handleTriggerClick.bind(this);
    this.onSubmit = this.stashRedirect.bind(this);

    this.overlay?.addEventListener('click', this.onOverlayClick);
    this.addEventListener('keyup', this.onEscape);
    this.querySelector('[data-enquiry-close]')?.addEventListener('click', this.onOverlayClick);
    document.addEventListener('click', this.onTriggerClick);
    this.form?.addEventListener('submit', this.onSubmit);
  }

  stashRedirect() {
    // The contact form round-trips and reloads with ?contact_posted=true; the
    // static handler at the bottom of this file then bounces to the thank-you page.
    try {
      sessionStorage.setItem('km-enquiry-redirect', this.dataset.enquiryRedirect || '/pages/thank-you');
    } catch (_) {
      /* sessionStorage unavailable — fall back to the form's own success message */
    }
    // No preventDefault — let the native submit proceed (carries the captcha token).
  }

  disconnectedCallback() {
    this.overlay?.removeEventListener('click', this.onOverlayClick);
    document.removeEventListener('click', this.onTriggerClick);
    document.body.classList.remove('overflow-hidden');
  }

  get gsap() {
    return window.gsap;
  }

  canAnimateWithGsap() {
    if (!this.gsap || !this.overlay || !this.drawerInner) return false;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('[data-enquire]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    this.populate(trigger.dataset.enquireTitle, trigger.dataset.enquireUrl);
    this.setMode(trigger.dataset.enquireMode);
    this.open(trigger);
  }

  populate(title, url) {
    this.currentTitle = (title || '').trim();
    if (this.productField) this.productField.value = this.currentTitle;
    if (this.urlField && url) {
      this.urlField.value = url.indexOf('http') === 0 ? url : `${window.location.origin}${url}`;
    }
  }

  setMode(mode) {
    const isWin = mode === 'win';
    this.mode = isWin ? 'win' : 'enquire';
    this.classList.toggle('enquiry-drawer--win', isWin);

    const title = this.currentTitle || 'this piece';
    if (this.heading) this.heading.textContent = isWin ? 'ENTER TO WIN' : 'ENQUIRE';

    if (this.intro) {
      const lead = isWin ? 'Enter to win' : 'Enquire about';
      const tail = isWin
        ? ". Pop in your details and you're in the draw."
        : ". Tell us a little about you and we’ll be in touch.";
      const span = document.createElement('span');
      span.className = 'enquiry-drawer__product';
      span.setAttribute('data-enquiry-product', '');
      span.textContent = title;
      this.intro.textContent = `${lead} `;
      this.intro.appendChild(span);
      this.intro.appendChild(document.createTextNode(tail));
    }

    // Enter-to-win collects name + email only, and tags the contact email.
    this.toggleField('phone', !isWin);
    this.toggleField('message', !isWin);
    if (this.submitLabel) this.submitLabel.textContent = isWin ? 'Enter draw' : 'Send enquiry';
    if (this.typeField) this.typeField.value = isWin ? 'Competition entry' : 'Enquiry';
  }

  toggleField(name, show) {
    const row = this.querySelector(`[data-enquiry-field="${name}"]`);
    if (!row) return;
    row.hidden = !show;
    const input = row.querySelector('input, textarea');
    if (input) input.disabled = !show; // disabled fields are omitted from the POST
  }

  trapDrawerFocus() {
    if (typeof trapFocus === 'function' && this.drawerInner) {
      trapFocus(document.getElementById('EnquiryDrawer'), this.drawerInner);
    }
  }

  open(triggeredBy) {
    this.activeElement = triggeredBy || document.activeElement;
    document.body.classList.add('overflow-hidden');
    this.isOpen = true;

    if (this.canAnimateWithGsap()) {
      this.openWithGsap();
      return;
    }

    this.classList.add('animate', 'active');
    window.setTimeout(() => {
      if (this.classList.contains('active')) this.trapDrawerFocus();
    }, 350);
  }

  openWithGsap() {
    const gsap = this.gsap;
    const motion = getEnquiryDrawerMotionConfig();
    const content = this.getAnimatedContentElements();

    this.gsapTimeline?.kill();
    this.classList.add('animate', 'active', 'animate-gsap');
    gsap.set(this, { visibility: 'visible' });
    gsap.set(this.overlay, { autoAlpha: 0 });
    gsap.set(this.drawerInner, { xPercent: 100, force3D: true });
    if (content.length) gsap.set(content, { autoAlpha: 0, force3D: true });

    this.gsapTimeline = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => this.trapDrawerFocus(),
    });

    this.gsapTimeline
      .to(this.overlay, { autoAlpha: 1, duration: motion.duration.overlay, ease: motion.ease.out }, 0)
      .to(this.drawerInner, { xPercent: 0, duration: motion.duration.drawerEnter, ease: motion.ease.emphasisOut }, 0);

    if (content.length) {
      this.gsapTimeline.to(content, {
        autoAlpha: 1,
        duration: motion.duration.drawerContent,
        ease: motion.ease.out,
        stagger: motion.stagger.drawerItems,
        clearProps: 'opacity,visibility,transform',
      }, 0.18);
    }
  }

  close() {
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.isOpen = false;

    if (this.canAnimateWithGsap() && this.classList.contains('active')) {
      this.closeWithGsap();
      return;
    }

    this.classList.remove('active');
  }

  closeWithGsap() {
    const gsap = this.gsap;
    const motion = getEnquiryDrawerMotionConfig();
    const content = this.getAnimatedContentElements();

    this.gsapTimeline?.kill();
    this.classList.add('animate-gsap');
    this.gsapTimeline = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => {
        this.classList.remove('active');
        gsap.set(this, { visibility: 'hidden' });
        if (content.length) gsap.set(content, { clearProps: 'opacity,visibility,transform' });
      },
    });

    if (content.length) {
      this.gsapTimeline.to(content, {
        autoAlpha: 0,
        duration: Math.max(0.2, motion.duration.drawerContent * 0.65),
        ease: motion.ease.in,
        stagger: { each: motion.stagger.drawerItems, from: 'end' },
      }, 0);
    }

    this.gsapTimeline
      .to(this.overlay, { autoAlpha: 0, duration: motion.duration.overlay, ease: motion.ease.in }, 0.06)
      .to(this.drawerInner, { xPercent: 100, duration: motion.duration.drawerExit, ease: motion.ease.emphasisInOut }, 0.06);
  }

  getAnimatedContentElements() {
    return Array.from(this.querySelectorAll('.drawer__header, .enquiry-drawer__intro, .contact-v2__row, .enquiry-drawer__submit, .contact-v2__status'));
  }
}

customElements.define('enquiry-drawer', EnquiryDrawer);

// After a drawer submission the contact form reloads the page with
// ?contact_posted=true. If we stashed a redirect target, bounce to it.
(function handleEnquiryRedirect() {
  let target = null;
  try {
    target = sessionStorage.getItem('km-enquiry-redirect');
  } catch (_) {
    return;
  }
  if (!target) return;

  const params = new URLSearchParams(window.location.search);
  if (params.get('contact_posted') === 'true') {
    try {
      sessionStorage.removeItem('km-enquiry-redirect');
    } catch (_) {
      /* ignore */
    }
    window.location.replace(target);
  }
})();
